import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { 
    Dialog, 
    DialogContent, 
    DialogDescription, 
    DialogFooter, 
    DialogHeader, 
    DialogTitle 
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { 
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Users, Plus, Pencil, Trash2, Loader2, ArrowLeft, Shield, Briefcase, HeadphonesIcon, UserCheck, UserX } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Role configuration
const ROLE_CONFIG = {
    admin: {
        label: 'Administrateur',
        icon: Shield,
        color: 'bg-purple-100 text-purple-700 border-purple-200'
    },
    commercial: {
        label: 'Commercial',
        icon: Briefcase,
        color: 'bg-blue-100 text-blue-700 border-blue-200'
    },
    assistante: {
        label: 'Assistante',
        icon: HeadphonesIcon,
        color: 'bg-emerald-100 text-emerald-700 border-emerald-200'
    }
};

export function UsersManagementPage() {
    const { user } = useAuth();
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isCreateOpen, setIsCreateOpen] = useState(false);
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [isDeleteOpen, setIsDeleteOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [formData, setFormData] = useState({ name: '', email: '', password: '', role: 'assistante', couleur: '#2e7cd6', telephone: '' });
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchUsers = async () => {
        try {
            const { data } = await axios.get(`${API}/api/admin/users`, { withCredentials: true });
            setUsers(data);
        } catch (error) {
            toast.error('Erreur lors du chargement des utilisateurs');
        } finally {
            setLoading(false);
        }
    };

    const fetchRoles = async () => {
        try {
            const { data } = await axios.get(`${API}/api/admin/roles`, { withCredentials: true });
            setRoles(data);
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    useEffect(() => {
        if (user?.role === 'admin') {
            fetchUsers();
            fetchRoles();
        }
    }, [user]);

    // Redirect non-admin users
    if (user?.role !== 'admin') {
        return <Navigate to="/" replace />;
    }

    const handleCreate = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            await axios.post(`${API}/api/admin/users`, formData, { withCredentials: true });
            toast.success('Utilisateur créé avec succès');
            setIsCreateOpen(false);
            setFormData({ name: '', email: '', password: '', role: 'assistante', couleur: '#2e7cd6', telephone: '' });
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleEdit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        try {
            const updateData = { ...formData };
            if (!updateData.password) delete updateData.password;
            await axios.put(`${API}/api/admin/users/${selectedUser.id}`, updateData, { withCredentials: true });
            toast.success('Utilisateur modifié avec succès');
            setIsEditOpen(false);
            setSelectedUser(null);
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setIsSubmitting(true);
        try {
            await axios.delete(`${API}/api/admin/users/${selectedUser.id}`, { withCredentials: true });
            toast.success('Utilisateur supprimé avec succès');
            setIsDeleteOpen(false);
            setSelectedUser(null);
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToggleActive = async (userItem) => {
        try {
            const { data } = await axios.patch(
                `${API}/api/admin/users/${userItem.id}/toggle-active`, 
                {}, 
                { withCredentials: true }
            );
            toast.success(data.message);
            fetchUsers();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la modification');
        }
    };

    const openEditDialog = (userItem) => {
        setSelectedUser(userItem);
        setFormData({ name: userItem.name, email: userItem.email, password: '', role: userItem.role, couleur: userItem.couleur || '#2e7cd6', telephone: userItem.telephone || '' });
        setIsEditOpen(true);
    };

    const openDeleteDialog = (userItem) => {
        setSelectedUser(userItem);
        setIsDeleteOpen(true);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        try {
            return new Date(dateStr).toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        } catch {
            return '-';
        }
    };

    const getRoleDisplay = (role) => {
        const config = ROLE_CONFIG[role] || ROLE_CONFIG.assistante;
        const Icon = config.icon;
        return (
            <Badge variant="outline" className={`${config.color} border`}>
                <Icon size={12} className="mr-1" />
                {config.label}
            </Badge>
        );
    };

    const activeUsers = users.filter(u => u.is_active !== false).length;
    const inactiveUsers = users.length - activeUsers;

    return (
        <div className="p-4 md:p-6 lg:p-8" data-testid="users-management-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div className="flex items-center gap-4">
                    <a 
                        href="/admin" 
                        className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
                        data-testid="back-to-admin"
                    >
                        <ArrowLeft size={20} className="text-slate-600" />
                    </a>
                    <div>
                        <h1 
                            className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight"
                            style={{ fontFamily: 'Manrope, sans-serif' }}
                            data-testid="users-title"
                        >
                            Gestion des utilisateurs
                        </h1>
                        <div className="flex items-center gap-4 mt-1">
                            <span className="text-slate-500 text-sm flex items-center gap-1">
                                <UserCheck size={14} className="text-emerald-500" />
                                {activeUsers} actif{activeUsers > 1 ? 's' : ''}
                            </span>
                            {inactiveUsers > 0 && (
                                <span className="text-slate-500 text-sm flex items-center gap-1">
                                    <UserX size={14} className="text-slate-400" />
                                    {inactiveUsers} inactif{inactiveUsers > 1 ? 's' : ''}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <Button
                    onClick={() => {
                        setFormData({ name: '', email: '', password: '', role: 'assistante', couleur: '#2e7cd6', telephone: '' });
                        setIsCreateOpen(true);
                    }}
                    className="bg-[#2e7cd6] hover:bg-blue-700 text-white"
                    data-testid="add-user-button"
                >
                    <Plus size={20} className="mr-2" />
                    Ajouter un utilisateur
                </Button>
            </div>

            {/* Users Table */}
            <Card className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-lg font-semibold text-slate-800">
                        <Users size={20} className="text-[#2e7cd6]" />
                        Liste des utilisateurs
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0 sm:p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="h-8 w-8 animate-spin text-[#2e7cd6]" />
                        </div>
                    ) : users.length === 0 ? (
                        <div className="text-center py-12 text-slate-500">
                            Aucun utilisateur trouvé
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <Table data-testid="users-table" className="min-w-[800px]">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead>Nom</TableHead>
                                        <TableHead>Email</TableHead>
                                        <TableHead>Rôle</TableHead>
                                        <TableHead>Couleur</TableHead>
                                        <TableHead>Statut</TableHead>
                                        <TableHead>Créé le</TableHead>
                                        <TableHead className="text-right">Actions</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {users.map((userItem) => (
                                        <TableRow 
                                            key={userItem.id} 
                                            data-testid={`user-row-${userItem.id}`}
                                            className={userItem.is_active === false ? 'opacity-60' : ''}
                                        >
                                            <TableCell className="font-medium">{userItem.name}</TableCell>
                                            <TableCell>{userItem.email}</TableCell>
                                            <TableCell>{getRoleDisplay(userItem.role)}</TableCell>
                                            <TableCell>
                                                {userItem.role === 'commercial' && userItem.couleur ? (
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-6 h-6 rounded-full border border-slate-300" style={{ backgroundColor: userItem.couleur }} data-testid={`user-color-${userItem.id}`} />
                                                        <span className="text-xs text-slate-500 font-mono">{userItem.couleur}</span>
                                                    </div>
                                                ) : (
                                                    <span className="text-xs text-slate-400">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <div className="flex items-center gap-2">
                                                    <Switch
                                                        checked={userItem.is_active !== false}
                                                        onCheckedChange={() => handleToggleActive(userItem)}
                                                        disabled={userItem.id === user.id}
                                                        data-testid={`toggle-active-${userItem.id}`}
                                                    />
                                                    <span className={`text-xs ${userItem.is_active !== false ? 'text-emerald-600' : 'text-slate-400'}`}>
                                                        {userItem.is_active !== false ? 'Actif' : 'Inactif'}
                                                    </span>
                                                </div>
                                            </TableCell>
                                            <TableCell>{formatDate(userItem.created_at)}</TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex items-center justify-end gap-2">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => openEditDialog(userItem)}
                                                        data-testid={`edit-user-${userItem.id}`}
                                                    >
                                                        <Pencil size={16} className="text-slate-600" />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => openDeleteDialog(userItem)}
                                                        disabled={userItem.id === user.id}
                                                        data-testid={`delete-user-${userItem.id}`}
                                                    >
                                                        <Trash2 size={16} className={userItem.id === user.id ? 'text-slate-300' : 'text-red-500'} />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Create User Dialog */}
            <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
                <DialogContent data-testid="create-user-dialog">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Nouvel utilisateur
                        </DialogTitle>
                        <DialogDescription>
                            Créez un nouveau compte utilisateur
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleCreate} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="create-name">Nom</Label>
                            <Input
                                id="create-name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="Prénom Nom"
                                required
                                data-testid="create-user-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="create-email">Email</Label>
                            <Input
                                id="create-email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                placeholder="email@bigdemenagement.fr"
                                required
                                data-testid="create-user-email"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="create-password">Mot de passe</Label>
                            <Input
                                id="create-password"
                                type="password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                placeholder="••••••••"
                                required
                                data-testid="create-user-password"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="create-role">Rôle</Label>
                            <Select
                                value={formData.role}
                                onValueChange={(value) => setFormData({ ...formData, role: value })}
                            >
                                <SelectTrigger data-testid="create-user-role">
                                    <SelectValue placeholder="Sélectionner un rôle" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="admin">
                                        <div className="flex items-center gap-2">
                                            <Shield size={14} className="text-purple-600" />
                                            Administrateur
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="commercial">
                                        <div className="flex items-center gap-2">
                                            <Briefcase size={14} className="text-blue-600" />
                                            Commercial
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="assistante">
                                        <div className="flex items-center gap-2">
                                            <HeadphonesIcon size={14} className="text-emerald-600" />
                                            Assistante
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {formData.role === 'commercial' && (
                        <div className="space-y-2">
                            <Label htmlFor="create-couleur">Couleur</Label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="color"
                                    id="create-couleur"
                                    value={formData.couleur}
                                    onChange={(e) => setFormData({ ...formData, couleur: e.target.value })}
                                    className="w-10 h-10 rounded cursor-pointer border border-slate-300"
                                    data-testid="create-user-couleur"
                                />
                                <Input
                                    value={formData.couleur}
                                    onChange={(e) => setFormData({ ...formData, couleur: e.target.value })}
                                    className="w-28 font-mono text-sm"
                                    placeholder="#2e7cd6"
                                />
                                <div className="w-8 h-8 rounded-full border border-slate-200" style={{ backgroundColor: formData.couleur }} />
                            </div>
                        </div>
                        )}
                        <div className="space-y-2">
                            <Label htmlFor="create-telephone">Telephone</Label>
                            <Input
                                id="create-telephone"
                                value={formData.telephone}
                                onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                                placeholder="06 XX XX XX XX"
                                data-testid="create-user-telephone"
                            />
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setIsCreateOpen(false)}
                            >
                                Annuler
                            </Button>
                            <Button
                                type="submit"
                                className="bg-[#2e7cd6] hover:bg-blue-700"
                                disabled={isSubmitting}
                                data-testid="create-user-submit"
                            >
                                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                Créer
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Edit User Dialog */}
            <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
                <DialogContent data-testid="edit-user-dialog">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Modifier l'utilisateur
                        </DialogTitle>
                        <DialogDescription>
                            Modifiez les informations de l'utilisateur
                        </DialogDescription>
                    </DialogHeader>
                    <form onSubmit={handleEdit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="edit-name">Nom</Label>
                            <Input
                                id="edit-name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                required
                                data-testid="edit-user-name"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit-email">Email</Label>
                            <Input
                                id="edit-email"
                                type="email"
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                required
                                data-testid="edit-user-email"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit-password">Nouveau mot de passe (laisser vide pour ne pas modifier)</Label>
                            <Input
                                id="edit-password"
                                type="password"
                                value={formData.password}
                                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                placeholder="••••••••"
                                data-testid="edit-user-password"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="edit-role">Rôle</Label>
                            <Select
                                value={formData.role}
                                onValueChange={(value) => setFormData({ ...formData, role: value })}
                            >
                                <SelectTrigger data-testid="edit-user-role">
                                    <SelectValue placeholder="Sélectionner un rôle" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="admin">
                                        <div className="flex items-center gap-2">
                                            <Shield size={14} className="text-purple-600" />
                                            Administrateur
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="commercial">
                                        <div className="flex items-center gap-2">
                                            <Briefcase size={14} className="text-blue-600" />
                                            Commercial
                                        </div>
                                    </SelectItem>
                                    <SelectItem value="assistante">
                                        <div className="flex items-center gap-2">
                                            <HeadphonesIcon size={14} className="text-emerald-600" />
                                            Assistante
                                        </div>
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        {formData.role === 'commercial' && (
                        <div className="space-y-2">
                            <Label htmlFor="edit-couleur">Couleur</Label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="color"
                                    id="edit-couleur"
                                    value={formData.couleur}
                                    onChange={(e) => setFormData({ ...formData, couleur: e.target.value })}
                                    className="w-10 h-10 rounded cursor-pointer border border-slate-300"
                                    data-testid="edit-user-couleur"
                                />
                                <Input
                                    value={formData.couleur}
                                    onChange={(e) => setFormData({ ...formData, couleur: e.target.value })}
                                    className="w-28 font-mono text-sm"
                                    placeholder="#2e7cd6"
                                />
                                <div className="w-8 h-8 rounded-full border border-slate-200" style={{ backgroundColor: formData.couleur }} />
                            </div>
                        </div>
                        )}
                        <div className="space-y-2">
                            <Label htmlFor="edit-telephone">Telephone</Label>
                            <Input
                                id="edit-telephone"
                                value={formData.telephone}
                                onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                                placeholder="06 XX XX XX XX"
                                data-testid="edit-user-telephone"
                            />
                        </div>
                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setIsEditOpen(false)}
                            >
                                Annuler
                            </Button>
                            <Button
                                type="submit"
                                className="bg-[#2e7cd6] hover:bg-blue-700"
                                disabled={isSubmitting}
                                data-testid="edit-user-submit"
                            >
                                {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                                Enregistrer
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
                <AlertDialogContent data-testid="delete-user-dialog">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
                        <AlertDialogDescription>
                            Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>{selectedUser?.name}</strong> ({selectedUser?.email}) ? 
                            Cette action est irréversible.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={handleDelete}
                            className="bg-red-500 hover:bg-red-600"
                            data-testid="confirm-delete-user"
                        >
                            {isSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}
